import { ApiMsgTypes } from "@/api/AppApi/common-interfaces/IResultStamp";
import {
  useClearCart,
  useGetCartQuery,
  useRemoveCartItem,
} from "@/api/AppApi/services/cart";
import { Col, Modal, Row } from "@/utils/bootstrap";
import SvgCloseModal from "./cart-item/manage-cart-item/SvgCloseModal";
import SpinnerForBtn from "../../../lib/loaders/SpinnerForBtn";
import CustomButton, {
  BtnRadius,
  BtnSizes,
  BtnTypes,
} from "../../../lib/CustomButton";
import { useTranslation } from "@/hooks/localization/useTranslations";
import { useIsMutating } from "@tanstack/react-query";
import useEventTrackers from "@/utils/helpers/pixel-helpers/hooks/useEventTrackers/useEventTrackers";
import { EventType } from "@/utils/helpers/pixel-helpers/hooks/useEventTrackers/helpers/interfaces";
import { useAtom } from "jotai";
import { currencyAtom } from "@/hooks/currencyAtom";

interface Props {
  show: boolean;
  setShow: (show: boolean) => void;
  id?: number;
  onOk?: () => void;
}
const ClearCartModal: React.FC<Props> = ({
  id = null,
  show,
  setShow,
  onOk = null,
}) => {
  const t = useTranslation();
  const getCart = useGetCartQuery();
  const [currency] = useAtom(currencyAtom);
  const { items, discount, tax, total, coupon, shipping_charge } =
    getCart?.data?.result ?? {};
  const { eventTrackers } = useEventTrackers();
  const isMutatinItem = useIsMutating({ mutationKey: ["post-item"] });
  const clearCartApi = useClearCart({
    onSuccess: (res) => {
      if (res?.message?.type === ApiMsgTypes.success) {
        onOk ? onOk() : setShow(false);
      }
    },
    onError: (_error) => {},
  });
  const removeItemApi = useRemoveCartItem({
    onSuccess: (res) => {
      if (res?.message?.type === ApiMsgTypes.success) {
        onOk ? onOk() : setShow(false);
      }
    },
    onError: (_error) => {},
  });
  const isLoading =
    clearCartApi.isLoading || removeItemApi.isLoading || !!isMutatinItem;
  const handleDelete = () => {
    if (id) {
      const item = items?.find((item) => item.id === id)!;
      removeItemApi.mutate(id);
      eventTrackers(EventType.remove_from_cart, [item], {
        discount: item.discount,
        tax: item.tax,
        value: item.price,
        shipping: shipping_charge,
        coupon: coupon?.code,
        currency: currency?.code,
      });
    } else {
      clearCartApi.mutate();
      eventTrackers(EventType.remove_from_cart, items!, {
        discount,
        tax,
        value: total,
        shipping: shipping_charge,
        coupon: coupon?.code,
        currency: currency?.code,
      });
    }
  };
  return (
    <Modal
      centered
      show={show}
      onHide={() => setShow(false)}
      dialogClassName="messagebox-dialog"
      contentClassName="clear-cart-dialog-content"
    >
      <div className="messagebox-closeicon">
        <SvgCloseModal onClick={() => setShow(false)} />
      </div>
      <div className="cart-Item-dialog-title">
        {id ? t.remove_item : t.clear_cart}
      </div>
      <div>{t.are_you_sure}</div>
      <Row className="m-0 w-100" style={{ gap: 12 }}>
        <Col className="p-0">
          <div className="w-100">
            <CustomButton
              type={BtnTypes.neutral}
              size={BtnSizes.large}
              radius={BtnRadius.small}
              onClick={() => setShow(false)}
              fullWidth
            >
              {t.keep}
            </CustomButton>
          </div>
        </Col>
        <Col className="p-0">
          <div className="w-100">
            <CustomButton
              type={BtnTypes.primary}
              size={BtnSizes.large}
              radius={BtnRadius.small}
              onClick={handleDelete}
              fullWidth
            >
              {isLoading ? <SpinnerForBtn /> : id ? t.remove : t.clear}
            </CustomButton>
          </div>
        </Col>
      </Row>
    </Modal>
  );
};

export default ClearCartModal;
